import Link from 'next/link'
import * as React from 'react'

import { cn } from '@/lib/utils'

import type { NonFsdsGeneralV1FaqItem } from '@/types/non-fsds-general-v1.type'

export interface FaqSectionProps {
  title: string
  description?: string
  contactLabel?: string
  contactUrl?: string
  defaultOpenId?: string
  faqs: NonFsdsGeneralV1FaqItem[]
  className?: string
}

/**
 * General V1 Q&A — Figma node 1:1612.
 *
 * Full-width header card with title, description and Contact CTA.
 * Four independent rounded accordion cards with 20px radius, 24px padding,
 * 0.5px border. Exclusive single-open behavior with plus/minus circle icons.
 */
export function FaqSection({
  title,
  description,
  contactLabel,
  contactUrl,
  defaultOpenId,
  faqs,
  className,
}: FaqSectionProps) {
  const [openId, setOpenId] = React.useState<string | null>(defaultOpenId || null)

  React.useEffect(() => {
    setOpenId(defaultOpenId || null)
  }, [defaultOpenId])

  if (!faqs || faqs.length === 0) return null

  const toggleItem = (id: string) => {
    setOpenId(prev => (prev === id ? null : id))
  }

  return (
    <section
      className={cn('w-full bg-[var(--gv1-bg-surface)] pb-[120px] pt-20', className)}
      data-testid="gv1-faq-section"
    >
      <div className="gv1-container">
        {/* Header card */}
        <div className="mb-2 flex flex-col items-start justify-between gap-4 rounded-[var(--gv1-radius-card)] border border-slate-200/70 bg-white p-[var(--gv1-card-padding)] sm:flex-row sm:items-center">
          <div>
            <h2
              className="text-2xl font-bold text-[var(--gv1-text-heading)]"
              style={{ fontFamily: 'var(--gv1-font-heading)' }}
            >
              {title}
            </h2>
            {description && (
              <p
                className="mt-2 text-sm text-[var(--gv1-text-body)]"
                style={{ fontFamily: 'var(--gv1-font-body)' }}
              >
                {description}
              </p>
            )}
          </div>
          {contactLabel && contactUrl && (
            <Link
              href={contactUrl}
              className="shrink-0 rounded-full bg-[var(--gv1-accent)] px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-[var(--gv1-accent)]/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gv1-accent)] focus-visible:ring-offset-2"
            >
              {contactLabel}
            </Link>
          )}
        </div>

        {/* Accordion cards */}
        <div className="flex flex-col gap-2">
          {faqs.map((faq) => {
            const isOpen = openId === faq.id

            return (
              <div
                key={faq.id}
                className={cn(
                  'overflow-hidden rounded-[var(--gv1-radius-card)] border bg-white',
                  isOpen ? 'min-h-[150px]' : 'min-h-[94px]'
                )}
                style={{ borderWidth: 'var(--gv1-border-thin)' }}
              >
                <button
                  type="button"
                  onClick={() => toggleItem(faq.id)}
                  className="flex w-full items-center justify-between p-[var(--gv1-card-padding)] text-left"
                  aria-expanded={isOpen}
                  aria-controls={`faq-content-${faq.id}`}
                  id={`faq-trigger-${faq.id}`}
                >
                  <span
                    className="pr-4 text-base font-semibold text-[var(--gv1-text-heading)]"
                    style={{ fontFamily: 'var(--gv1-font-heading)' }}
                  >
                    {faq.q}
                  </span>
                  <span
                    className={cn(
                      'flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-bold transition-colors duration-200',
                      isOpen
                        ? 'bg-[var(--gv1-accent)] text-white'
                        : 'bg-gray-100 text-gray-500'
                    )}
                    aria-hidden="true"
                  >
                    {isOpen ? '−' : '+'}
                  </span>
                </button>

                <div
                  id={`faq-content-${faq.id}`}
                  role="region"
                  aria-labelledby={`faq-trigger-${faq.id}`}
                  aria-hidden={!isOpen}
                  className={cn(
                    'grid transition-all duration-300',
                    isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                  )}
                >
                  <div className="overflow-hidden">
                    <div
                      className="px-[var(--gv1-card-padding)] pb-[var(--gv1-card-padding)] text-sm leading-5 text-[var(--gv1-text-body)]"
                      style={{ fontFamily: 'var(--gv1-font-body)' }}
                    >
                      {faq.a}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
